import React, {useEffect, useState} from 'react';
import {ClientsContract, clientsContractsService} from '@store/clients/contracts';
import ModalComponent from '@components/modal/Modal.component';
import {Button, CircularProgress, IconButton, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import {ClientsService} from '@store/clients/services';
import NewTableComponent from '@components/table/NewTable.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import {useNavigate} from 'react-router-dom';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';

interface ExistingContractModalProps {
  contractId: number | string;
  handleClose: () => void;
  handleForce?: () => void;
  handleUseContract?: (contractId: number | string, contractName: string) => void;
}

export const ExistingContractModal = (props: ExistingContractModalProps) => {
  const {handleClose, contractId, handleUseContract, handleForce} = props;
  const {t} = useTranslation();
  const navigate = useNavigate();

  const [loading] = useState<boolean>(false);
  const [contract, setContract] = useState<ClientsContract | undefined>(undefined);

  useEffect(() => {
    if (contractId) {
      clientsContractsService.getContractById(contractId)
        .subscribe({
          next: setContract
        });
    }
  }, [contractId]);

  return <ModalComponent
    aria-label="over-drawer"
    handleClose={handleClose}
    maxWidth="xl"
    title={t('clients.contracts.existing.title')}
    content={
      <>
        {loading && <CircularProgress/>}
        {!loading &&
            <Stack pt="20px" pb="20px" height="85vh" width="100%" direction="column" spacing={2} overflow="hidden"
                   alignItems="center">
                <Stack direction="row" spacing={2}>
                    <TextFieldComponent color="secondary"
                                        label={t('clients.details.contracts.manage.name')}
                                        readOnly
                                        value={contract?.contractName}/>
                    <TextFieldComponent color="secondary"
                                        label={t('clients.details.contracts.manage.clientUnit')}
                                        readOnly
                                        value={contract?.clientOrganisationUnitName}/>
                    <TextFieldComponent color="secondary"
                                        label={t('clients.details.contracts.manage.reference')}
                                        readOnly
                                        value={contract?.contractReference}/>
                    <TextFieldComponent color="secondary"
                                        label={t('clients.details.contractedServices.manage.idDatahub')}
                                        readOnly
                                        value={contract?.datahubSpec?.idDatahub}/>
                    <TextFieldComponent color="secondary"
                                        label={t('clients.details.contractedServices.manage.additionalInformation')}
                                        readOnly
                                        value={contract?.datahubSpec?.additionalInformation}/>
                </Stack>
                <NewTableComponent
                    loading={loading}
                    columns={[
                      COLUMN_TYPES.TEXT_BOLD({field: 'id', label: t('clients.servicesTable.id'), width: 100}),
                      COLUMN_TYPES.TEXT_BOLD({
                        field: 'service.clientWasteName',
                        label: t('clients.servicesTable.name'),
                        sortable: true,
                        valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.clientWasteName,
                        width: 350,
                      }),
                      COLUMN_TYPES.TEXT({
                        field: 'schedulesCount',
                        label: t('clients.servicesTable.frequency'),
                        valueGetter: (params: GridValueGetterParams<any, ClientsService>) =>
                          (params.value ?? 0) > 0 ? t('global.frequencies.recurrent') : t('global.frequencies.onDemand'),
                        width: 130,
                      }),
                      COLUMN_TYPES.TEXT({
                        field: 'kind.kind',
                        label: t('clients.servicesTable.type'),
                        sortable: true,
                        valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.kindName,
                        width: 250,
                      }),
                      COLUMN_TYPES.TEXT({
                        field: 'waste.name',
                        label: t('clients.servicesTable.details'),
                        sortable: true,
                        valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.wasteName,
                        width: 100,
                      }),
                      COLUMN_TYPES.TEXT({
                        field: 'container.name',
                        label: t('clients.servicesTable.container'),
                        sortable: true,
                        valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.containerName,
                        width: 200,
                      }),
                      COLUMN_TYPES.CUSTOM({
                        field: 'Service',
                        label: t('clients.servicesTable.service'),
                        renderCell: (params) => {
                          const row = params.row as ClientsService;

                          return (
                            <IconButton onClick={() => navigate(`/clients/contracts/services/${row.id}`)}>
                              <ArrowCircleRightOutlinedIcon color="primary"/>
                            </IconButton>
                          );
                        },
                        width: 100,
                      }),
                    ]}
                    disableRowSelection
                    paginationMode="client"
                    disablePagination
                    data={contract?.services}
                />
                <Stack justifyContent="center" direction="row" spacing={2}>
                    <Button variant="outlined" color="primary" onClick={handleClose}>
                      {t('global.cancel')}
                    </Button>
                  {contractId && handleUseContract && handleForce && <><Button variant="contained" color="primary"
                                                                               onClick={() => handleUseContract(contractId, contract?.contractName || '')}>
                    {t('clients.contracts.existing.useContract')}
                  </Button>
                      <Button variant="contained" color="primary" onClick={() => handleForce()}>
                        {t('clients.contracts.existing.forceContractCreation')}
                      </Button></>}
                </Stack>
            </Stack>}
      </>
    }
  />;
};

export default ExistingContractModal;
